import { Checkbox, TableCellProps, TableRow, Theme } from '@mui/material';
import { groupBy } from 'lodash';
import { Fragment } from 'react';
import { EMPTY_VALUE } from 'shared/constants';
import {
  CheckboxOnChangeEvent,
  Column,
  TableRowOnClickEvent,
} from 'shared/types';
import { OptionalIds, TableProps } from '../types';
import { StyledRowCell } from '../../../../EstimatedBudgetsTable/styles';
import { formatNumberWithSpaces } from '../../EstimatedTVBudgetTable/utils/formatNumberWithSpaces';

type Cell =
  | string
  | number
  | boolean
  | { type: 'budget'; value: number; currency: string }
  | { type: 'performance'; dsp: number; adServer: number };

const renderCell = ({
  format,
  cell,
  renderer,
}: Partial<Column> & {
  cell: Cell;
}) => {
  if (typeof cell === 'object' && cell !== null) {
    if (cell.type === 'budget') {
      return `${formatNumberWithSpaces(cell.value)}`;
    }

    if (cell.type === 'performance') {
      return `DSP: ${cell.dsp}, AdServer: ${cell.adServer}`;
    }
  }

  const value = cell || EMPTY_VALUE;

  if (format) {
    return format(value, 'PLN');
  }
  if (renderer) {
    return renderer(cell);
  }
  return value;
};

export const renderRows = ({
  handleRowClick,
  optionalIds,
  theme,
  rows,
  columns,
}: {
  rows: TableProps['rows'];
  columns: TableProps['columns'];
  theme: Theme;
  optionalIds: OptionalIds;
  handleRowClick: (
    e: TableRowOnClickEvent | CheckboxOnChangeEvent,
    rowId: string
  ) => void;
}) => {
  const groupedRows = groupBy(rows, 'format');
  return Object.entries(groupedRows).flatMap(([_, groupRows], index) => {
    const rowColor =
      index % 2 === 0
        ? theme.palette.tableBackground.main
        : theme.palette.common.white;

    return groupRows.map((row) => (
      <TableRow
        key={row.format + row.variant}
        onClick={(e) => handleRowClick(e, row.format + row.variant)}
        sx={{ backgroundColor: rowColor }}
      >
        <StyledRowCell padding="checkbox">
          <Checkbox
            color="primary"
            checked={
              row.isRequired || optionalIds[row.format + row.variant] || false
            }
            disabled={row.isRequired}
            onChange={(e) => handleRowClick(e, row.format + row.variant)}
          />
        </StyledRowCell>
        {columns.map(({ key, format, align, renderer, padding }) => {
          return (
            <Fragment key={`${row.format + row.variant}${key}`}>
              <StyledRowCell
                align={align as TableCellProps['align']}
                sx={{ padding }}
              >
                {renderCell({
                  format,
                  cell:
                    key === 'budget'
                      ? {
                          type: 'budget',
                          value: row[key].value,
                          currency: row[key].currency,
                        }
                      : (row[key] as Cell),
                  renderer,
                })}
              </StyledRowCell>
            </Fragment>
          );
        })}
      </TableRow>
    ));
  });
};
